import { isAsciiDigit, isJavaLetter, isJavaLetterOrDigit, isJavaWhitespace, javaStrip } from '../javaText.ts';
import type { Random } from './random.ts';

/**
 * Dice expressions (docs/PARITY-STEP-2-PLAN.md section 3): a port of the desktop's
 * `dice.DiceExpression`, so a roll means the same in both editions and, with the same
 * seeded `JavaRandom`, comes out the same - schema/fixtures/dice-cases.txt holds the
 * desktop's own results. What the Dice Roller rolls, and later what sheets and random
 * tables use.
 *
 * The notation: `d20`, `3d6`, `d%`, `4dF`, computed counts or sides `(@level)d6`; keep and
 * drop `4d6kh3`, `k3`, `2d20kl1`, `4d6dl1`, `5d10dh2`; exploding `3d6!`, `5d10!>=9`; rerolls
 * `2d6r1`, `2d6ro<3`; success pools `6d10>=7`; `+ - * /` (whole numbers, `/` rounding
 * down) and parentheses; `@name` from a resolver; `min`, `max`, `abs` and
 * `if(condition, then, otherwise)`, which rolls only the branch it picks.
 *
 * Numbers are Java longs: 64-bit, as `bigint`, with the same "too big" errors. Only ASCII
 * digits are digits (Java also reads other scripts' digits; nobody types those in a roll).
 */

export class DiceError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DiceError';
  }
}

/** Supplies `@name` values; null or undefined for a name it doesn't know. */
export type Resolver = (name: string) => bigint | null | undefined;

export const MAX_DICE = 1000;
export const MAX_SIDES = 1_000_000;
const MAX_REPEATS = 100;
const NO_REFERENCES: Resolver = () => null;

const LONG_MIN = -(2n ** 63n);
const LONG_MAX = 2n ** 63n - 1n;
const INT_MIN = -(2 ** 31);
const INT_MAX = 2 ** 31 - 1;

/** One die as it was rolled; a rerolled or dropped die still shows, struck through. */
export interface Die {
  readonly value: number;
  readonly dropped: boolean;
  readonly rerolled: boolean;
  readonly exploded: boolean;
  readonly success: boolean;
}

/** Counts toward its group's total (or its successes). */
export const counts = (die: Die) => !die.dropped && !die.rerolled;

/** One group of dice from a roll; `sides` is 0 for Fate dice; `value` is the successes when it counts them. */
export interface DiceGroupResult {
  readonly notation: string;
  readonly sides: number;
  readonly dice: readonly Die[];
  readonly value: bigint;
  readonly countsSuccesses: boolean;
}

/** The one die a group kept, if it kept exactly one. */
function singleKeptDie(group: DiceGroupResult): number | null {
  const kept = group.dice.filter(counts);
  return kept.length === 1 ? kept[0]!.value : null;
}

/** A single kept die showing its highest face: a natural 20 on `1d20+5`. */
export const isNaturalMax = (group: DiceGroupResult) => group.sides > 1 && singleKeptDie(group) === group.sides;
/** A single kept die showing a 1. */
export const isNaturalMin = (group: DiceGroupResult) => group.sides > 1 && singleKeptDie(group) === 1;

export interface RollResult {
  readonly expression: string;
  readonly total: bigint;
  /** The expression with each group's dice and each `@field`'s value in place: `1d20[14] + @athletics(5)`. */
  readonly breakdown: string;
  readonly groups: readonly DiceGroupResult[];
}

/** `1d20[14] + 5 = 19`, or just `19` when there was nothing to show. */
export function describeRoll(result: RollResult): string {
  return result.breakdown === String(result.total) ? result.breakdown : `${result.breakdown} = ${result.total}`;
}

/** Draws a line through text (dropped and rerolled dice) with combining long strokes. */
export function strike(text: string): string {
  let out = '';
  for (const c of text) out += `${c}̶`;
  return out;
}

// Java's character classes (javaText.ts); only ASCII digits are digits in a roll.
const isDigit = isAsciiDigit;
const isLetter = isJavaLetter;
const isLetterOrDigit = isJavaLetterOrDigit;
const isWhitespace = isJavaWhitespace;
const strip = javaStrip;

function checked(value: bigint): bigint {
  if (value < LONG_MIN || value > LONG_MAX) throw new DiceError('That number is too big.');
  return value;
}

function floorDiv(a: bigint, b: bigint): bigint {
  if (a === LONG_MIN && b === -1n) return LONG_MIN; // Java's Math.floorDiv overflows silently here
  const q = a / b;
  return (a % b !== 0n && (a < 0n) !== (b < 0n)) ? q - 1n : q;
}

// ---- evaluation ----

interface Value {
  readonly number: bigint;
  readonly text: string;
}

interface Context {
  readonly random: Random | null;
  readonly resolver: Resolver;
  readonly groups: DiceGroupResult[];
  diceRolled: number;
}

const newContext = (random: Random | null, resolver: Resolver): Context => ({ random, resolver, groups: [], diceRolled: 0 });

interface Node {
  eval(context: Context): Value;
  hasDice(): boolean;
  collectReferences(names: Set<string>): void;
}

class Constant implements Node {
  constructor(readonly value: bigint) {}
  eval(): Value {
    return { number: this.value, text: String(this.value) };
  }
  hasDice() {
    return false;
  }
  collectReferences() {}
}

class Reference implements Node {
  constructor(readonly name: string) {}
  eval(context: Context): Value {
    const value = context.resolver(this.name);
    if (value === null || value === undefined) throw new DiceError(`There's no field called @${this.name}.`);
    return { number: value, text: `@${this.name}(${value})` };
  }
  hasDice() {
    return false;
  }
  collectReferences(names: Set<string>) {
    names.add(this.name);
  }
}

class Group implements Node {
  constructor(readonly inner: Node) {}
  eval(context: Context): Value {
    const value = this.inner.eval(context);
    return { number: value.number, text: `(${value.text})` };
  }
  hasDice() {
    return this.inner.hasDice();
  }
  collectReferences(names: Set<string>) {
    this.inner.collectReferences(names);
  }
}

class Negate implements Node {
  constructor(readonly inner: Node) {}
  eval(context: Context): Value {
    const value = this.inner.eval(context);
    return { number: checked(-value.number), text: `-${value.text}` };
  }
  hasDice() {
    return this.inner.hasDice();
  }
  collectReferences(names: Set<string>) {
    this.inner.collectReferences(names);
  }
}

class Binary implements Node {
  constructor(
    readonly op: '+' | '-' | '*' | '/',
    readonly left: Node,
    readonly right: Node,
  ) {}
  eval(context: Context): Value {
    const a = this.left.eval(context);
    const b = this.right.eval(context);
    let result: bigint;
    if (this.op === '+') result = checked(a.number + b.number);
    else if (this.op === '-') result = checked(a.number - b.number);
    else if (this.op === '*') result = checked(a.number * b.number);
    else {
      if (b.number === 0n) throw new DiceError("Can't divide by zero.");
      result = floorDiv(a.number, b.number);
    }
    const shown = this.op === '*' ? '×' : this.op === '/' ? '÷' : this.op;
    return { number: result, text: `${a.text} ${shown} ${b.text}` };
  }
  hasDice() {
    return this.left.hasDice() || this.right.hasDice();
  }
  collectReferences(names: Set<string>) {
    this.left.collectReferences(names);
    this.right.collectReferences(names);
  }
}

class Call implements Node {
  constructor(
    readonly fn: string,
    readonly args: readonly Node[],
  ) {}
  eval(context: Context): Value {
    if (this.fn === 'if') {
      // Lazy: only the chosen branch is evaluated, so only its dice are rolled.
      const condition = this.args[0]!.eval(context);
      const chosen = this.args[condition.number !== 0n ? 1 : 2]!.eval(context);
      return { number: chosen.number, text: chosen.text };
    }
    const values = this.args.map((arg) => arg.eval(context));
    const numbers = values.map((v) => v.number);
    const result =
      this.fn === 'min' ? numbers.reduce((x, y) => (y < x ? y : x)) : this.fn === 'max' ? numbers.reduce((x, y) => (y > x ? y : x)) : checked(numbers[0]! < 0n ? -numbers[0]! : numbers[0]!);
    return { number: result, text: `${this.fn}(${values.map((v) => v.text).join(', ')})` };
  }
  hasDice() {
    return this.args.some((arg) => arg.hasDice());
  }
  collectReferences(names: Set<string>) {
    this.args.forEach((arg) => arg.collectReferences(names));
  }
}

type KeepKind = 'KEEP_HIGHEST' | 'KEEP_LOWEST' | 'DROP_HIGHEST' | 'DROP_LOWEST';
type CompareOp = '>' | '>=' | '<' | '<=' | '=' | 'max';

/** A comparison against a number, or against an `@field` read when rolling (`d%<=@climb`). */
class Compare {
  constructor(
    readonly op: CompareOp,
    readonly value: number,
    readonly reference: string | null = null,
  ) {}
  resolve(context: Context): Compare {
    if (this.reference === null) return this;
    const resolved = context.resolver(this.reference);
    if (resolved === null || resolved === undefined) throw new DiceError(`There's no field called @${this.reference}.`);
    return new Compare(this.op, Number(resolved < BigInt(INT_MIN) ? BigInt(INT_MIN) : resolved > BigInt(INT_MAX) ? BigInt(INT_MAX) : resolved));
  }
  matches(roll: number): boolean {
    switch (this.op) {
      case '>':
        return roll > this.value;
      case '>=':
        return roll >= this.value;
      case '<':
        return roll < this.value;
      case '<=':
        return roll <= this.value;
      default:
        return roll === this.value;
    }
  }
}

const die = (value: number): Die => ({ value, dropped: false, rerolled: false, exploded: false, success: false });

/** One group of dice; `suffix` is its modifiers as written, to show a computed group as rolled (`2d6kh1`). */
class Dice implements Node {
  constructor(
    readonly notation: string,
    readonly suffix: string,
    readonly count: Node | null,
    readonly sides: Node | null,
    readonly fate: boolean,
    readonly keep: KeepKind | null,
    readonly keepCount: number,
    readonly explode: Compare | null,
    readonly reroll: Compare | null,
    readonly rerollOnce: boolean,
    readonly success: Compare | null,
  ) {}

  eval(context: Context): Value {
    if (context.random === null) throw new DiceError(`"${this.notation}" rolls dice, so it has no fixed value.`);
    const diceCount = this.count === null ? 1n : this.count.eval(context).number;
    if (diceCount < 0n || diceCount > BigInt(MAX_DICE)) {
      throw new DiceError(`${this.notation}: can roll between 0 and ${MAX_DICE} dice, not ${diceCount}.`);
    }
    let faces = 0;
    if (!this.fate) {
      const sideCount = this.sides!.eval(context).number;
      if (sideCount < 1n || sideCount > BigInt(MAX_SIDES)) {
        throw new DiceError(`${this.notation}: a die needs between 1 and ${MAX_SIDES} sides, not ${sideCount}.`);
      }
      faces = Number(sideCount);
    }
    const low = this.fate ? -1 : 1;
    const high = this.fate ? 1 : faces;
    const explode = this.explode?.resolve(context) ?? null;
    const reroll = this.reroll?.resolve(context) ?? null;
    const success = this.success?.resolve(context) ?? null;
    if (explode && matchesEveryFace(explode, low, high)) throw new DiceError(`${this.notation}: every roll would explode, forever.`);
    if (reroll && matchesEveryFace(reroll, low, high)) throw new DiceError(`${this.notation}: every roll would be rerolled, forever.`);

    const dice: Die[] = [];
    let pending = Number(diceCount);
    let explosions = 0;
    while (pending > 0) {
      pending--;
      let value = rollOne(context, low, high);
      let rerolls = 0;
      while (reroll && reroll.matches(value) && rerolls < (this.rerollOnce ? 1 : MAX_REPEATS)) {
        dice.push({ ...die(value), rerolled: true });
        value = rollOne(context, low, high);
        rerolls++;
      }
      let rolled = die(value);
      if (explode && explode.matches(value) && explosions < MAX_REPEATS) {
        rolled = { ...rolled, exploded: true };
        explosions++;
        pending++;
      }
      dice.push(rolled);
    }

    this.applyKeep(dice);
    let total = 0n;
    for (let i = 0; i < dice.length; i++) {
      let d = dice[i]!;
      if (success) {
        d = { ...d, success: counts(d) && success.matches(d.value) };
        dice[i] = d;
        if (d.success) total++;
      } else if (counts(d)) {
        total += BigInt(d.value);
      }
    }
    const computed = (this.count !== null && !(this.count instanceof Constant)) || (this.sides !== null && !(this.sides instanceof Constant));
    const shown = computed ? `${diceCount}d${this.fate ? 'F' : faces}${this.suffix}` : this.notation;
    context.groups.push({ notation: shown, sides: this.fate ? 0 : faces, dice, value: total, countsSuccesses: success !== null });
    return { number: total, text: `${shown}[${this.describe(dice)}]` };
  }

  private applyKeep(dice: Die[]): void {
    if (this.keep === null) return;
    const active: number[] = [];
    dice.forEach((d, i) => counts(d) && active.push(i));
    // Lowest first; ties keep their rolled order (a stable sort), so the result is stable.
    active.sort((a, b) => dice[a]!.value - dice[b]!.value);
    const n = active.length;
    const k = Math.min(this.keepCount, n);
    const toDrop =
      this.keep === 'KEEP_HIGHEST' ? active.slice(0, n - k) : this.keep === 'KEEP_LOWEST' ? active.slice(k, n) : this.keep === 'DROP_HIGHEST' ? active.slice(n - k, n) : active.slice(0, k);
    for (const index of toDrop) dice[index] = { ...dice[index]!, dropped: true };
  }

  private describe(dice: readonly Die[]): string {
    return dice
      .map((d) => {
        let text = this.fate ? (d.value > 0 ? '+' : d.value < 0 ? '-' : '0') : String(d.value);
        if (d.exploded) text += '!';
        if (d.success) text += '*';
        return counts(d) ? text : strike(text);
      })
      .join(', ');
  }

  hasDice() {
    return true;
  }

  collectReferences(names: Set<string>) {
    this.count?.collectReferences(names);
    this.sides?.collectReferences(names);
    for (const compare of [this.explode, this.reroll, this.success]) {
      if (compare?.reference) names.add(compare.reference);
    }
  }
}

function rollOne(context: Context, low: number, high: number): number {
  if (++context.diceRolled > MAX_DICE * 10) throw new DiceError("That's too many dice to roll at once.");
  return low + context.random!.nextInt(high - low + 1);
}

function matchesEveryFace(compare: Compare, low: number, high: number): boolean {
  for (let face = low; face <= high; face++) if (!compare.matches(face)) return false;
  return true;
}

/** `3d6!`: explodes on the die's highest face, known only once the sides are evaluated (`2d(@die)!`). */
class MaxExplodingDice implements Node {
  constructor(readonly dice: Dice) {}
  eval(context: Context): Value {
    if (context.random === null) return this.dice.eval(context);
    // As the desktop does: the sides are evaluated in a context of their own.
    const sides = this.dice.sides!.eval(newContext(context.random, context.resolver)).number;
    const max = Number(sides < 1n ? 1n : sides > BigInt(MAX_SIDES) ? BigInt(MAX_SIDES) : sides);
    const d = this.dice;
    return new Dice(d.notation, d.suffix, d.count, new Constant(BigInt(max)), false, d.keep, d.keepCount, new Compare('=', max), d.reroll, d.rerollOnce, d.success).eval(context);
  }
  hasDice() {
    return true;
  }
  collectReferences(names: Set<string>) {
    this.dice.collectReferences(names);
  }
}

// ---- parsing ----

class Parser {
  pos = 0;
  constructor(readonly text: string) {}

  parseExpression(): Node {
    let node = this.parseTerm();
    for (;;) {
      this.skipSpaces();
      const c = this.peek();
      if (c === '+' || c === '-') {
        this.pos++;
        node = new Binary(c, node, this.parseTerm());
      } else return node;
    }
  }

  parseTerm(): Node {
    let node = this.parseUnary();
    for (;;) {
      this.skipSpaces();
      const c = this.peek();
      if (c === '*' || c === '/' || c === '×' || c === '÷') {
        this.pos++;
        node = new Binary(c === '*' || c === '×' ? '*' : '/', node, this.parseUnary());
      } else return node;
    }
  }

  parseUnary(): Node {
    this.skipSpaces();
    if (this.peek() === '-') {
      this.pos++;
      return new Negate(this.parseUnary());
    }
    if (this.peek() === '+') {
      this.pos++;
      return this.parseUnary();
    }
    return this.parsePostfix();
  }

  parsePostfix(): Node {
    this.skipSpaces();
    const start = this.pos;
    if (this.isDiceStart()) return this.parseDice(start, null);
    const primary = this.parsePrimary();
    if (this.isDiceStart() && (primary instanceof Constant || primary instanceof Group)) {
      return this.parseDice(start, primary instanceof Group ? primary.inner : primary);
    }
    return primary;
  }

  parsePrimary(): Node {
    this.skipSpaces();
    const c = this.peek();
    if (isDigit(c)) {
      const start = this.pos;
      while (isDigit(this.peek())) this.pos++;
      if (this.peek() === '.') throw this.error('Only whole numbers work here');
      const value = BigInt(this.text.slice(start, this.pos));
      if (value > LONG_MAX) throw this.error('That number is too big');
      return new Constant(value);
    }
    if (c === '@') {
      this.pos++;
      const name = this.identifier();
      if (name === '') throw this.error('Expected a field name after @');
      return new Reference(name);
    }
    if (c === '(') {
      this.pos++;
      const inner = this.parseExpression();
      this.skipSpaces();
      if (this.peek() !== ')') throw this.error('Missing a closing )');
      this.pos++;
      return new Group(inner);
    }
    if (isLetter(c)) {
      const start = this.pos;
      const word = this.identifier().toLowerCase();
      if (word !== 'min' && word !== 'max' && word !== 'abs' && word !== 'if') {
        this.pos = start;
        throw this.error(`Didn't understand "${word}" - fields start with @, like @${word}`);
      }
      this.skipSpaces();
      if (this.peek() !== '(') throw this.error(`${word} needs parentheses, like ${word}(1, 2)`);
      this.pos++;
      const args = [this.parseExpression()];
      this.skipSpaces();
      while (this.peek() === ',') {
        this.pos++;
        args.push(this.parseExpression());
        this.skipSpaces();
      }
      if (this.peek() !== ')') throw this.error('Missing a closing )');
      this.pos++;
      if (word === 'abs' && args.length !== 1) throw this.error('abs takes one value');
      if (word === 'if' && args.length !== 3) throw this.error('if takes three values: if(condition, then, otherwise)');
      return new Call(word, args);
    }
    if (c === '\0') throw this.error('The roll ends too early');
    throw this.error(`Didn't expect "${c}"`);
  }

  isDiceStart(): boolean {
    if (this.peek() !== 'd' && this.peek() !== 'D') return false;
    const next = this.pos + 1 < this.text.length ? this.text[this.pos + 1]! : '\0';
    return isDigit(next) || next === '%' || next === 'F' || next === 'f' || next === '(';
  }

  parseDice(start: number, count: Node | null): Node {
    this.pos++; // the 'd'
    let sides: Node | null = null;
    let fate = false;
    const c = this.peek();
    if (c === '%') {
      this.pos++;
      sides = new Constant(100n);
    } else if (c === 'F' || c === 'f') {
      this.pos++;
      fate = true;
    } else if (c === '(') {
      sides = this.parsePrimary();
    } else {
      const digits = this.pos;
      while (isDigit(this.peek())) this.pos++;
      const value = BigInt(this.text.slice(digits, this.pos));
      if (value > LONG_MAX) throw this.error('That number is too big');
      sides = new Constant(value);
    }

    const suffixStart = this.pos;
    let keep: KeepKind | null = null;
    let keepCount = 1;
    let explode: Compare | null = null;
    let reroll: Compare | null = null;
    let rerollOnce = false;
    let success: Compare | null = null;
    for (;;) {
      const rest = this.text.slice(this.pos).toLowerCase();
      const kind: KeepKind | null = rest.startsWith('kh')
        ? 'KEEP_HIGHEST'
        : rest.startsWith('kl')
          ? 'KEEP_LOWEST'
          : rest.startsWith('dh')
            ? 'DROP_HIGHEST'
            : rest.startsWith('dl')
              ? 'DROP_LOWEST'
              : rest.startsWith('k')
                ? 'KEEP_HIGHEST'
                : null;
      if (kind !== null) {
        if (keep !== null) throw this.error('Use only one keep or drop per group of dice');
        keep = kind;
        this.pos += rest.startsWith('k') && !rest.startsWith('kh') && !rest.startsWith('kl') ? 1 : 2;
        keepCount = isDigit(this.peek()) ? this.number() : 1;
      } else if (rest.startsWith('!')) {
        if (fate) throw this.error("Fate dice can't explode");
        this.pos++;
        explode = this.optionalCompare() ?? new Compare('max', 0);
      } else if (rest.startsWith('ro') || rest.startsWith('r')) {
        rerollOnce = rest.startsWith('ro');
        this.pos += rerollOnce ? 2 : 1;
        reroll = this.optionalCompare();
        if (reroll === null) throw this.error('Say which rolls to reroll, like r1 or r<3');
      } else if (rest.startsWith('>') || rest.startsWith('<') || rest.startsWith('=')) {
        if (success !== null) throw this.error('Use only one success target per group of dice');
        success = this.optionalCompare();
      } else break;
    }
    const notation = this.text.slice(start, this.pos).replaceAll(' ', '');
    const suffix = this.text.slice(suffixStart, this.pos).replaceAll(' ', '');
    if (explode?.op === 'max') {
      return new MaxExplodingDice(new Dice(notation, suffix, count, sides, fate, keep, keepCount, null, reroll, rerollOnce, success));
    }
    return new Dice(notation, suffix, count, sides, fate, keep, keepCount, explode, reroll, rerollOnce, success);
  }

  /** `>=5`, `<3`, `=1`, or a bare `1` meaning `=1`; null if none follows. */
  optionalCompare(): Compare | null {
    let op: CompareOp;
    if (this.text.startsWith('>=', this.pos) || this.text.startsWith('<=', this.pos)) {
      op = this.text.slice(this.pos, this.pos + 2) as CompareOp;
      this.pos += 2;
    } else if (this.peek() === '>' || this.peek() === '<' || this.peek() === '=') {
      op = this.peek() as CompareOp;
      this.pos++;
    } else if (isDigit(this.peek()) || this.peek() === '@') {
      op = '=';
    } else return null;
    const negative = this.peek() === '-';
    if (negative) this.pos++;
    if (this.peek() === '@' && !negative) {
      this.pos++;
      const name = this.identifier();
      if (name === '') throw this.error('Expected a field name after @');
      return new Compare(op, 0, name);
    }
    if (!isDigit(this.peek())) throw this.error(`Expected a number after ${op}`);
    const value = this.number();
    return new Compare(op, negative ? -value : value);
  }

  number(): number {
    const start = this.pos;
    while (isDigit(this.peek())) this.pos++;
    const value = Number(this.text.slice(start, this.pos));
    if (!Number.isSafeInteger(value) || value > INT_MAX) throw this.error('That number is too big');
    return value;
  }

  identifier(): string {
    const start = this.pos;
    while (this.pos < this.text.length && (isLetterOrDigit(this.text[this.pos]!) || this.text[this.pos] === '_')) this.pos++;
    return this.text.slice(start, this.pos);
  }

  skipSpaces(): void {
    while (this.pos < this.text.length && isWhitespace(this.text[this.pos]!)) this.pos++;
  }

  peek(): string {
    return this.pos < this.text.length ? this.text[this.pos]! : '\0';
  }

  error(message: string): DiceError {
    return new DiceError(`${message} (at character ${this.pos + 1} of "${this.text}").`);
  }
}

/** A parsed dice expression: parse once, roll as often as needed. */
export class DiceExpression {
  private constructor(
    readonly source: string,
    private readonly root: Node,
  ) {}

  /** Throws a `DiceError` saying where, if `text` isn't a valid expression. */
  static parse(text: string | null | undefined): DiceExpression {
    const source = strip(text ?? '');
    if (source === '') throw new DiceError('Type a roll, like 1d20+5 or 4d6kh3.');
    const parser = new Parser(source);
    const root = parser.parseExpression();
    parser.skipSpaces();
    if (parser.pos < source.length) throw parser.error(`Didn't expect "${source.slice(parser.pos)}"`);
    return new DiceExpression(source, root);
  }

  static isValid(text: string): boolean {
    try {
      DiceExpression.parse(text);
      return true;
    } catch {
      return false;
    }
  }

  /** Whether rolling it involves dice, as opposed to arithmetic over numbers and fields. */
  hasDice(): boolean {
    return this.root.hasDice();
  }

  /** Every `@name` it reads, in order of first appearance. */
  references(): Set<string> {
    const names = new Set<string>();
    this.root.collectReferences(names);
    return names;
  }

  roll(random: Random, resolver: Resolver = NO_REFERENCES): RollResult {
    const context = newContext(random, resolver);
    const value = this.root.eval(context);
    return { expression: this.source, total: value.number, breakdown: value.text, groups: context.groups };
  }

  /** Evaluates an expression with no dice: a derived field's formula. */
  evaluate(resolver: Resolver = NO_REFERENCES): bigint {
    if (this.hasDice()) throw new DiceError(`"${this.source}" rolls dice, so it has no fixed value.`);
    return this.root.eval(newContext(null, resolver)).number;
  }

  toString(): string {
    return this.source;
  }
}
